import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, ListPlus, ChevronDown, ChevronRight, Trash2, Pencil } from "lucide-react";
import { Category, Subcategory } from "@/types/category";
import { useCategories } from "@/hooks/useCategories";
import CategoryForm from "@/components/admin/categories/CategoryForm";
import { getIconByName } from "@/utils/iconMapping";
import { useAdminCheckQuery } from "@/hooks/useAdminCheckQuery";
import React from "react";

const ManageCategories = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);
  const [editingCategory, setEditingCategory] = useState<{ id: number; label: string; icon?: string } | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<{ id: number; label: string; parent_category: number } | null>(null);
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false);
  const [isAddSubcategoryOpen, setIsAddSubcategoryOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState<number | null>(null);
  const [deletingSubcategory, setDeletingSubcategory] = useState<number | null>(null);

  const { data: isAdmin } = useAdminCheckQuery();

  const { 
    loading, 
    categories, 
    subcategories, 
    subcategoriesByParent, 
    refreshCategories 
  } = useCategories();

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getSubcategoriesForCategory = (categoryId: number) => {
    return subcategoriesByParent[categoryId] || [];
  };

  const addCategoryMutation = useMutation({
    mutationFn: async (data: { label: string; icon: string }) => {
      const { error } = await supabase.from("categories").insert([
        { 
          label: data.label, 
          icon: data.icon,
          order: (categories.length + 1) 
        }
      ]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({ title: "Success", description: "Category added successfully" });
      setIsAddCategoryOpen(false);
    },
    onError: (error: Error) => {
      console.error("Error adding category:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to add category. Please check your permissions.",
      });
    },
  });

  const addSubcategoryMutation = useMutation({
    mutationFn: async ({ label, parentCategory }: { label: string; parentCategory: number }) => {
      const existingSubcategories = subcategoriesByParent[parentCategory] || [];
      const newDisplayOrder = existingSubcategories.length > 0 
        ? Math.max(...existingSubcategories.map(s => s.display_order || 0)) + 1 
        : 1;
      
      const { error } = await supabase.from("subcategories").insert([
        {
          label,
          parent_category: parentCategory,
          display_order: newDisplayOrder
        }
      ]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({ title: "Success", description: "Subcategory added successfully" });
      setIsAddSubcategoryOpen(false);
    },
    onError: (error: Error) => {
      console.error("Error adding subcategory:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to add subcategory. Please check your permissions.",
      });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (categoryId: number) => {
      const associatedSubcategories = getSubcategoriesForCategory(categoryId);
      
      if (associatedSubcategories.length > 0) {
        throw new Error("Cannot delete category with subcategories. Please delete all subcategories first.");
      }

      const { data: subcatsWithParent, error: subcatError } = await supabase
        .from("subcategories")
        .select("id")
        .eq("parent_category", categoryId);
        
      if (subcatError) throw subcatError;
      
      if (subcatsWithParent && subcatsWithParent.length > 0) {
        throw new Error(`Cannot delete category with ${subcatsWithParent.length} associated subcategories. Delete those first.`);
      }

      const { error } = await supabase
        .from("categories")
        .delete()
        .eq("id", categoryId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({ title: "Success", description: "Category deleted successfully" });
      setDeletingCategory(null);
    },
    onError: (error: Error) => {
      console.error("Error deleting category:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
      setDeletingCategory(null);
    },
  });

  const deleteSubcategoryMutation = useMutation({
    mutationFn: async (subcategoryId: number) => {
      const { data: services, error: servicesError } = await supabase
        .from("services")
        .select("id")
        .eq("subcategory", subcategoryId);
        
      if (servicesError) throw servicesError;
      
      if (services && services.length > 0) {
        throw new Error(`Cannot delete subcategory used by ${services.length} services. Please reassign these services first.`);
      }

      const { error } = await supabase
        .from("subcategories")
        .delete()
        .eq("id", subcategoryId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({ title: "Success", description: "Subcategory deleted successfully" });
      setDeletingSubcategory(null);
    },
    onError: (error: Error) => {
      console.error("Error deleting subcategory:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
      setDeletingSubcategory(null);
    },
  });

  const editCategoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { label: string; icon: string } }) => {
      const { error } = await supabase
        .from("categories")
        .update({ label: data.label, icon: data.icon })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({ title: "Success", description: "Category updated successfully" });
      setEditingCategory(null);
    },
    onError: (error: Error) => {
      console.error("Error updating category:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update category. Please check your permissions.",
      });
    },
  });

  const editSubcategoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { label: string; parentCategory: number } }) => {
      const { error } = await supabase
        .from("subcategories")
        .update({ label: data.label, parent_category: data.parentCategory })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast({ title: "Success", description: "Subcategory updated successfully" });
      setEditingSubcategory(null);
    },
    onError: (error: Error) => {
      console.error("Error updating subcategory:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update subcategory. Please check your permissions.",
      });
    },
  });

  const handleAddCategory = (data: { label: string; icon: string }) => {
    addCategoryMutation.mutate(data);
  };

  const handleAddSubcategory = (data: { label: string; icon: string }) => {
    const selectedCategoryId = categories.length > 0 ? categories[0].id : 0;
    addSubcategoryMutation.mutate({ 
      label: data.label, 
      parentCategory: selectedCategoryId 
    });
  };

  const renderCategoryIcon = (iconName: string | null) => {
    if (!iconName) return null;
    
    const IconComponent = getIconByName(iconName);
    if (!IconComponent) return null;
    
    return React.createElement(IconComponent, { className: "w-4 h-4 mr-2" });
  };

  if (isAdmin === false) {
    return (
      <AdminLayout title="Categories">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <h2 className="text-xl font-semibold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You need admin privileges to manage categories.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Categories">
      <div className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex gap-2">
            <Dialog open={isAddCategoryOpen} onOpenChange={setIsAddCategoryOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Category</DialogTitle>
                </DialogHeader>
                <CategoryForm onSubmit={handleAddCategory} />
              </DialogContent>
            </Dialog>

            <Dialog open={isAddSubcategoryOpen} onOpenChange={setIsAddSubcategoryOpen}>
              <DialogTrigger asChild>
                <Button>
                  <ListPlus className="w-4 h-4 mr-2" />
                  Add Subcategory
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Subcategory</DialogTitle>
                </DialogHeader>
                <CategoryForm 
                  onSubmit={handleAddSubcategory} 
                  isSubcategory 
                  categories={categories} 
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center py-4">
                    Loading categories...
                  </TableCell>
                </TableRow>
              ) : categories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} className="text-center py-4">
                    No categories found. Add your first category!
                  </TableCell>
                </TableRow>
              ) : (
                categories.map((category) => (
                  <>
                    <TableRow key={`category-${category.id}`} className="hover:bg-gray-50">
                      <TableCell>
                        <button 
                          onClick={() => toggleCategory(category.id)}
                          className="flex items-center gap-2"
                        >
                          {expandedCategories.includes(category.id) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <div className="flex items-center">
                            {renderCategoryIcon(category.icon)}
                            <span className="font-medium">{category.label}</span>
                          </div>
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingCategory({ 
                                  id: category.id, 
                                  label: category.label || "", 
                                  icon: category.icon || ""
                                })}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Edit Category</DialogTitle>
                              </DialogHeader>
                              {editingCategory && (
                                <CategoryForm
                                  initialData={{
                                    id: editingCategory.id,
                                    label: editingCategory.label,
                                    icon: editingCategory.icon
                                  }}
                                  onSubmit={(data) => {
                                    editCategoryMutation.mutate({
                                      id: editingCategory.id,
                                      data: data
                                    });
                                  }}
                                  isEditing
                                />
                              )}
                            </DialogContent>
                          </Dialog>
                          
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setDeletingCategory(category.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    {expandedCategories.includes(category.id) && 
                      getSubcategoriesForCategory(category.id).map((subcategory) => (
                        <TableRow key={`subcategory-${subcategory.id}`} className="bg-gray-50">
                          <TableCell>
                            <div className="ml-8 text-sm text-gray-600 flex items-center">
                              {subcategory.label}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEditingSubcategory({
                                      id: subcategory.id,
                                      label: subcategory.label || "",
                                      parent_category: subcategory.parent_category,
                                    })}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Edit Subcategory</DialogTitle>
                                  </DialogHeader>
                                  {editingSubcategory && (
                                    <CategoryForm
                                      initialData={{
                                        id: editingSubcategory.id,
                                        label: editingSubcategory.label || "",
                                      }}
                                      isSubcategory
                                      categories={categories}
                                      parentCategoryId={editingSubcategory.parent_category}
                                      onSubmit={(data) => {
                                        editSubcategoryMutation.mutate({
                                          id: editingSubcategory.id,
                                          data: {
                                            label: data.label,
                                            parentCategory: editingSubcategory.parent_category,
                                          },
                                        });
                                      }}
                                      isEditing
                                    />
                                  )}
                                </DialogContent>
                              </Dialog>
                              
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => setDeletingSubcategory(subcategory.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    }
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={deletingCategory !== null} onOpenChange={(open) => !open && setDeletingCategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this category? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deletingCategory && deleteCategoryMutation.mutate(deletingCategory)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deletingSubcategory !== null} onOpenChange={(open) => !open && setDeletingSubcategory(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this subcategory? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deletingSubcategory && deleteSubcategoryMutation.mutate(deletingSubcategory)}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default ManageCategories;
